import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import { useBooleanState, usePlanFeature } from "hooks";
import { WithRenderTracking } from "components/DatadogTracker";

import UpgradeModal from "./UpgradeModal";

function WithUpgradePlan(WrappedComponent, componentName = null) {
  const TrackedUpgradeModal = WithRenderTracking(UpgradeModal, componentName);

  const WithUpgrade = ({
    featureName,
    featureTitle,
    loading,
    disabled,
    upgradeSubject,
    onClick,
    email,
    description,
    content,
    ...props
  }) => {
    const [isRefreshing, startRefreshing, stopRefreshing] = useBooleanState();
    const [isUpgradeModalOpened, openUpgradeModal, closeUpgradeModal] =
      useBooleanState();
    const {
      loading: loadingFeature,
      eligibility: { eligible, cause } = {},
      error,
      reloadData,
    } = usePlanFeature(featureName, { fetchOnInit: false });

    const actionDecider = useCallback(() => {
      if (!error && eligible) onClick();
      if (!error && !eligible) openUpgradeModal();
    }, [eligible, error, onClick, openUpgradeModal]);

    useEffect(() => {
      if (loadingFeature) startRefreshing();
    }, [loadingFeature, startRefreshing]);

    useEffect(() => {
      if (isRefreshing && !loadingFeature) {
        stopRefreshing();
        actionDecider();
      }
    }, [isRefreshing, loadingFeature, stopRefreshing, actionDecider]);

    return (
      <React.Fragment>
        <WrappedComponent
          loading={loading || isRefreshing}
          disabled={disabled || isRefreshing}
          onClick={reloadData}
          {...props}
        />
        {isUpgradeModalOpened && (
          <TrackedUpgradeModal
            email={email}
            title={featureTitle}
            message={cause}
            upgradeSubject={upgradeSubject}
            description={description}
            onClose={closeUpgradeModal}
            content={content}
          />
        )}
      </React.Fragment>
    );
  };

  WithUpgrade.propTypes = {
    onClick: PropTypes.func.isRequired,
    featureName: PropTypes.string.isRequired,
    featureTitle: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    upgradeSubject: PropTypes.string,
    email: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
  };

  WithUpgrade.defaultProps = {
    disabled: false,
    loading: false,
    upgradeSubject: undefined,
    email: undefined,
    content: undefined,
    description: "",
  };

  return WithUpgrade;
}

export default WithUpgradePlan;
